import React, { RefObject, useCallback, useEffect, useState } from 'react';
import { Divider } from 'src/components/atoms';
import {
  Accordion,
  AccordionSubtitle,
  CategoryFooter,
  CategoryName,
} from 'src/components/molecules';
import { CategoryType } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { FeatureFlag, HumanReadableLanguages } from 'src/types';
import { useTheme } from 'styled-components';
import { CategoryBody } from '../CategoryBody';
import { CATEGORY_CONFIGURATION_V2_NAME_MAX_LENGTH } from '../UpdateCategoryModal/helpers';
import { DividerContainer } from './Category.styles';
import { getCategoryStatusBadgeAppearance } from './helpers/getCategoryStatusBadgeAppearance';

interface Props {
  category: CategoryType;
  currentLocale: keyof typeof HumanReadableLanguages;
  setCurrentCategory: React.Dispatch<React.SetStateAction<CategoryType | null>>;
  currentCategory: CategoryType | null;
  isCurrentCategory: boolean;
  isCurrentCategoryChanged: boolean;
  unnamedCategoryCount: number;
  updateCategory: () => void;
  setUpdateCategoryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  newCategory: number | null;
  newCategoryRef?: RefObject<HTMLDivElement | null>;
  onSaveTitle: (
    id: number,
    name: string,
    setName: React.Dispatch<string>,
  ) => void;
  onClickCategory: () => void;
  recalculateCoordinates: () => void;
}

export const Category = ({
  category,
  currentLocale,
  setCurrentCategory,
  currentCategory,
  isCurrentCategory,
  unnamedCategoryCount,
  updateCategory,
  setUpdateCategoryModalOpen,
  isCurrentCategoryChanged,
  newCategory,
  newCategoryRef,
  onSaveTitle,
  onClickCategory,
  recalculateCoordinates,
}: Props) => {
  const theme = useTheme();
  const [defaultFlowIntakeValid, setDefaultFlowIntakeValid] = useState(true);
  const [inlineNameAtMaxLength, setInlineNameAtMaxLength] = useState(false);

  const dynamicQuestionsV1 = useIsFeatureEnabled(
    FeatureFlag.DYNAMIC_QUESTIONS_V1,
  );

  useEffect(() => {
    if (!isCurrentCategory) {
      setDefaultFlowIntakeValid(true);
      setInlineNameAtMaxLength(false);
    }
  }, [isCurrentCategory]);

  const handleAtMaxNameLengthChange = useCallback((atMax: boolean) => {
    setInlineNameAtMaxLength(atMax);
  }, []);

  const categoryHasTitle = !!category.locales?.[currentLocale]?.name;
  const showCategoryUntitledError =
    isCurrentCategory && isCurrentCategoryChanged && !categoryHasTitle;
  const showCategoryNameMaxLengthError =
    isCurrentCategory && inlineNameAtMaxLength && !showCategoryUntitledError;
  const showAccordionSubtitleError =
    showCategoryUntitledError || showCategoryNameMaxLengthError;
  const accordionSubtitleErrorMessage = (() => {
    if (showCategoryUntitledError) {
      return 'Category name required';
    }
    if (showCategoryNameMaxLengthError) {
      return `Maximum ${CATEGORY_CONFIGURATION_V2_NAME_MAX_LENGTH} characters reached`;
    }
    return '';
  })();
  const isSaveDisabled =
    !categoryHasTitle ||
    !isCurrentCategoryChanged ||
    (dynamicQuestionsV1 && isCurrentCategory && !defaultFlowIntakeValid);

  const handleSaveCategory = () => {
    if (isCurrentCategory) {
      updateCategory();
    }
  };

  const disableCategoryChanges = Boolean(category.hasActiveLinkedForm);

  return (
    <>
      <Accordion
        title={
          <CategoryName
            category={category}
            currentLocale={currentLocale}
            onSave={onSaveTitle}
            defaultValue={`Untitled Category ${unnamedCategoryCount}`}
            maxNameLength={CATEGORY_CONFIGURATION_V2_NAME_MAX_LENGTH}
            onAtMaxNameLengthChange={handleAtMaxNameLengthChange}
            disabled
          />
        }
        shadow
        topBorderRadius="12px"
        bottomBorderRadius="12px"
        defaultOpen={isCurrentCategory}
        preventChange={isCurrentCategoryChanged}
        onClick={onClickCategory}
        data-testid={`category-accordion-${category.id}`}
        subtitle={
          <AccordionSubtitle
            date={category.createdAt}
            showError={showAccordionSubtitleError}
            author={
              category.Author?.displayName || category.Author?.name || 'Vault'
            }
            disabled={
              !category.isDeterminedCategory && !category.isReportedCategory
            }
            errorMessage={accordionSubtitleErrorMessage}
            liveBadgeConfig={
              category.isDeterminedCategory || category.isReportedCategory
                ? getCategoryStatusBadgeAppearance(category.status, theme)
                : undefined
            }
          />
        }
        accordionRef={category.id === newCategory ? newCategoryRef : null}
        showError={showAccordionSubtitleError}
        topBorder
        last
        openCallback={recalculateCoordinates}
      >
        <CategoryBody
          category={category}
          setCurrentCategory={setCurrentCategory}
          currentCategory={currentCategory}
          isCurrentCategory={isCurrentCategory}
          onDefaultFlowIntakeValidityChange={
            dynamicQuestionsV1 ? setDefaultFlowIntakeValid : undefined
          }
        />
        <DividerContainer>
          <Divider
            variant="horizontal"
            color={
              theme.isVaultVariant
                ? theme.newPalette.neutral.N300
                : theme.atlas.core.color.gray[200]
            }
          />
        </DividerContainer>
        <CategoryFooter
          isSaveDisabled={isSaveDisabled}
          currentCategory={currentCategory}
          setUpdateCategoryModalOpen={setUpdateCategoryModalOpen}
          saveCategory={handleSaveCategory}
          isCurrentCategory={isCurrentCategory}
          isCtaDisabled={disableCategoryChanges}
        />
      </Accordion>
    </>
  );
};
